import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { alreadyTranslatedText } from '@execonline-inc/translations';
import { Decoder, array, dateISO, field, number, oneOf, string, succeed } from 'jsonous';
import { resourceDecoder, resourceWithMetadataDecoder } from '../../../../../../Resource/Decoders';
import {
  attributeResourceDecoder,
  experienceTypeDecoder,
  offeringTypeDecoder,
  programInstanceOptionsDecoder,
  supportedLanguageResourceDecoder,
} from '../../../../Decoders';
import { Status } from '../../../../Types';
import {
  Experience,
  ExperienceResource,
  ExperiencesResource,
  ExperiencesWithFilterOptions,
  FilterOptionData,
  Metadata,
  ShowUnlicensedToggle,
  SortOptionValue,
} from './Types';

export const filterOptionDecoder: Decoder<FilterOptionData> = succeed({})
  .assign('label', field('label', string))
  .assign('value', field('value', string))
  .assign('count', field('count', number));

export const sortOptionValueDecoder: Decoder<SortOptionValue> = oneOf([
  stringLiteral<SortOptionValue>('title-asc'),
  stringLiteral<SortOptionValue>('title-desc'),
  stringLiteral<SortOptionValue>('trending-desc'),
]);

export const statusDecoder: Decoder<Status> = oneOf([
  stringLiteral<Status>('Licensed'),
  stringLiteral<Status>('Unlicensed'),
]);

export const showUnlicensedToggleDecoder: Decoder<ShowUnlicensedToggle> = oneOf([
  stringLiteral<ShowUnlicensedToggle>('show'),
  stringLiteral<ShowUnlicensedToggle>('hide'),
]);

const experienceDecoder: Decoder<Experience> = succeed({})
  .assign('id', field('id', number))
  .assign('kind', field('kind', experienceTypeDecoder))
  .assign('shortName', field('short_name', explicitMaybe(string)))
  .assign('descriptor', field('descriptor', explicitMaybe(string)))
  .assign('title', field('title', alreadyTranslatedText))
  .assign('description', field('description', alreadyTranslatedText))
  .assign('totalHours', field('total_hours', explicitMaybe(number)))
  .assign('hoursPerWeek', field('hours_per_week', explicitMaybe(number)))
  .assign('schoolPartnerLogoUrl', field('school_partner_logo_url', explicitMaybe(string)))
  .assign('schoolPartnerName', field('school_partner_name', string))
  .assign('publicDetailedDescriptionHtml', field('public_detailed_description_html', string))
  .assign('featured', field('featured', explicitMaybe(dateISO)))
  .assign('competencies', field('competencies', array(attributeResourceDecoder)))
  .assign(
    'availableOnDemandInstanceId',
    field('available_on_demand_instance_id', explicitMaybe(number)),
  )
  .assign(
    'supportedLanguages',
    field('supported_languages', array(supportedLanguageResourceDecoder)),
  )
  .assign(
    'programInstanceOptions',
    field('program_instance_options', programInstanceOptionsDecoder),
  )
  .assign('offeringType', field('offering_type', offeringTypeDecoder))
  .assign('status', field('status', statusDecoder));

const experienceResourceDecoder: Decoder<ExperienceResource> = resourceDecoder(experienceDecoder);

const experiencesMetadataDecoder: Decoder<Metadata> = succeed({}).assign(
  'resultsCount',
  field('results_count', number),
);

const experiencesWithFilterOptionsDecoder: Decoder<ExperiencesWithFilterOptions> = succeed({})
  .assign('experiences', field('experiences', array(experienceResourceDecoder)))
  .assign(
    'productTypeFilterOptions',
    field('product_type_filter_options', array(filterOptionDecoder)),
  )
  .assign('competencyFilterOptions', field('competency_filter_options', array(filterOptionDecoder)))
  .assign('durationFilterOptions', field('duration_filter_options', array(filterOptionDecoder)))
  .assign(
    'schoolPartnerFilterOptions',
    field('school_partner_filter_options', array(filterOptionDecoder)),
  )
  .assign(
    'availableLanguagesFilterOptions',
    field('available_languages_filter_options', array(filterOptionDecoder)),
  )
  .assign('showUnlicensedToggle', field('show_unlicensed_toggle', showUnlicensedToggleDecoder));

export const experiencesResourceDecoder: Decoder<ExperiencesResource> = resourceWithMetadataDecoder(
  experiencesWithFilterOptionsDecoder,
  experiencesMetadataDecoder,
);
